import { Typography } from "@deere/fuel-react";
import { Option, Select } from "@deere/fuel-react";
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { toast } from "react-toastify";
import _ from "lodash";
import moment from "moment";

import DateMatrixComponent from "./DateMatrixComponent";
import { updateFOMStatusAPI } from "../api";

const DateAndStatusesContent = React.memo(
  forwardRef((props, ref) => {
    const {
      tabValue,
      isEditClicked,
      isUserAdmin,
      isEditable,
      setIsLoading,
      getShipToDetailsForMachine,
    } = props;

    // Form data state
    const [formData, setFormData] = useState({
      status: '',
      ataDate: '',
      atdDate: '',
      plannedLoadingDate: ''
    });

    // Form errors state
    const [formErrors, setFormErrors] = useState({
      status: '',
      ataDate: '',
      atdDate: '',
      plannedLoadingDate: '',
      dateComparison: ''
    });

    // Handle form field changes
    const handleFormFieldChange = (fieldName, value) => {
      setFormData(prev => ({
        ...prev,
        [fieldName]: value
      }));

      // Clear specific error when field is updated
      if (formErrors[fieldName]) {
        setFormErrors(prev => ({
          ...prev,
          [fieldName]: '',
          dateComparison: fieldName === 'ataDate' || fieldName === 'atdDate' ? '' : prev.dateComparison
        }));
      }
    };

    // Form data validation method - returns true if valid, false if invalid
    const validateFormData = () => {
      const errors = {
        status: '',
        ataDate: '',
        atdDate: '',
        plannedLoadingDate: '',
        dateComparison: ''
      };
      let isValid = true;

      // Status-based validation
      if (formData.status === "DEPARTED" && (!formData.atdDate || formData.atdDate === "")) {
        errors.atdDate = '* Please select ATD DATE to make status as DEPARTED.';
        isValid = false;
      }
      if (formData.status === "DELIVERED" && (!formData.ataDate || formData.ataDate === "")) {
        errors.ataDate = '* Please select ATA DATE to make status as DELIVERED.';
        isValid = false;
      }

      // Date relationship validation
      if (formData.ataDate && formData.atdDate) {
        if (moment(formData.atdDate).isAfter(moment(formData.ataDate), 'day')) {
          errors.dateComparison = '* ATD DATE should be less than or equal to ATA DATE.';
          isValid = false;
        }
      }

      setFormErrors(errors);
      return isValid;
    };

    const handleStatusChange = (value) => {
      handleFormFieldChange('status', value);
    };

    const handleADDDateChange = (newAddDate) => {
      const isEmpty = newAddDate == null || _.isEmpty(newAddDate);
      handleFormFieldChange('ataDate', isEmpty ? '' : newAddDate);
    };

    const handleATDDateChange = (newATDDate) => {
      const isEmpty = newATDDate == null || _.isEmpty(newATDDate);
      handleFormFieldChange('atdDate', isEmpty ? '' : newATDDate);
    };

    const handleLoadBuildDateChange = (newLoadBuildDate) => {
      const isEmpty = newLoadBuildDate == null || _.isEmpty(newLoadBuildDate);
      handleFormFieldChange('plannedLoadingDate', isEmpty ? '' : newLoadBuildDate);
    };

    // Initialize status based on pathStatus only - no automatic setting based on dates
    const initialStatus = tabValue.pathStatus || "NO STATUS";

    // Legacy state variables (keeping for compatibility)
    const [selectedStatus, setSelectedStatus] = useState(initialStatus);
    const [selectedDate, setSelectedDate] = useState(tabValue.addTime);
    const [forwarderNtf, setFowarderNtf] = useState(tabValue.forwarderNtf);
    const [forwarderNtfStatus, setForwarderntfStatus] = useState(
      tabValue.forwarderNtfStatus
    );
    const [selectedATDDate, setSelectedATDDate] = useState(tabValue.ATD_FOM);
    const [selectedLoadBuildDate, setSelectedLoadBuildDate] = useState(
      tabValue.ETD_FOM
    );

    useEffect(() => {
      // Initialize form data with tabValue - no automatic status setting
      const initialStatusForForm = tabValue.pathStatus || "NO STATUS";

      setFormData({
        status: initialStatusForForm,
        ataDate: tabValue.addTime ? tabValue.addTime.split("T")[0] : '',
        atdDate: tabValue.ATD_FOM || '',
        plannedLoadingDate: tabValue.ETD_FOM || ''
      });

      // Update legacy state for compatibility
      setSelectedStatus(initialStatusForForm);
      setSelectedDate(tabValue.addTime ? tabValue.addTime.split("T")[0] : null);
      setSelectedATDDate(tabValue.ATD_FOM || null);
      setSelectedLoadBuildDate(tabValue.ETD_FOM || null);
      setFowarderNtf(tabValue.forwarderNtf || null);
      setForwarderntfStatus(tabValue.forwarderNtfStatus || null);

      // Clear form errors when data loads
      setFormErrors({
        status: '',
        ataDate: '',
        atdDate: '',
        plannedLoadingDate: '',
        dateComparison: ''
      });
    }, [tabValue]);

    // Remove automatic status change on ATA/ATD date change in edit mode
    // Only set status automatically on initial load (see useEffect for tabValue)

    const handleSave = async () => {
      // Validate form data using the validation method
      const isFormValid = validateFormData();

      if (!isFormValid) {
        // Validation failed, form errors are already set by validateFormData
        return;
      }

      // Use the status from form data directly without automatic changes
      let statusToSend = formData.status;
      
      const addTimeAsDate = formData.ataDate ? new Date(formData.ataDate) : null;
      const ATDDateAsDate = formData.atdDate ? new Date(formData.atdDate) : null;
      const loadBuildDateAsDate = formData.plannedLoadingDate
        ? new Date(formData.plannedLoadingDate)
        : null;

      // Calculate final status to send
      const finalStatusToSend = statusToSend == tabValue.pathStatus || statusToSend == "NO STATUS" ? null : statusToSend;

      // Update legacy state variables for compatibility
      setSelectedStatus(statusToSend);
      setSelectedDate(formData.ataDate || null);
      setSelectedATDDate(formData.atdDate || null);
      setSelectedLoadBuildDate(formData.plannedLoadingDate || null);
      try {
        setIsLoading(true);
        
        
        // Call the API to update the FOM Status and dates
        const response = await updateFOMStatusAPI(
          tabValue.serial17,
          tabValue.orderPathId,
          tabValue.buildId,
          statusToSend == tabValue.pathStatus || statusToSend == "NO STATUS" ? null : statusToSend,
          addTimeAsDate,
          ATDDateAsDate,
          loadBuildDateAsDate,
          tabValue.orderUnit,
          tabValue.orderNumber
        );
        if (response.status === 200) {
          toast.success(response.data.body);
          setIsLoading(false);
        } else {
          toast.error(response.data.body);
          setIsLoading(false);
        }
      } catch (error) {
        toast.error("An error occurred while updating FOM Status.");
        setIsLoading(false);
      }
      getShipToDetailsForMachine(
        [tabValue.orderUnit, tabValue.orderNumber].join("")
      );
    };

    const onCloseResetValuesIfNotSaved = () => {
      // Reset form data to original tabValue - no automatic status setting
      const initialStatusForForm = tabValue.pathStatus || "NO STATUS";

      setFormData({
        status: initialStatusForForm,
        ataDate: tabValue.addTime ? tabValue.addTime.split("T")[0] : '',
        atdDate: tabValue.ATD_FOM || '',
        plannedLoadingDate: tabValue.ETD_FOM || ''
      });

      // Reset legacy state for compatibility
      setSelectedStatus(tabValue.pathStatus);
      setSelectedDate(tabValue.addTime ? tabValue.addTime.split("T")[0] : null);
      setSelectedATDDate(tabValue.ATD_FOM);
      setSelectedLoadBuildDate(tabValue.ETD_FOM);

      // Clear form errors
      setFormErrors({
        status: '',
        ataDate: '',
        atdDate: '',
        plannedLoadingDate: '',
        dateComparison: ''
      });
    }

    useImperativeHandle(
      ref,
      () => ({
        handleSave,
        onCloseResetValuesIfNotSaved, validateFormData
      }),
      [handleSave, onCloseResetValuesIfNotSaved]
    );

    return (
      <div className="flex flex-col h-auto w-full max-w-[1000px] overflow-hidden">
        {/* Row for Status, ATA Date, and Forwarder Notify Date */}
        <div className="flex items-start mb-4 pl-5">
          <div className="flex flex-col gap-1 w-[260px]">
            <Typography fontWeight={"700"} fontSize={14}>
              FOM STATUS
            </Typography>
            {isEditClicked ? (
              <Select
                value={formData.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="w-full"
              >
                <Option value="PLAN">PLAN</Option>
                <Option value="FORECAST">FORECAST</Option>
                <Option value="CONFIRM">CONFIRM</Option>
                <Option value="PICKUP_READY">PICKUP READY</Option>
                <Option value="DEPARTED">DEPARTED</Option>
                <Option value="DELIVERED">DELIVERED</Option>
                <Option value="ARCHIVE">ARCHIVE</Option>
              </Select>
            ) : (
              <Typography fontWeight="400" fontSize={16}>
                {formData.status === "PICKUP_READY"
                  ? "PICKUP READY"
                  : formData.status === "AT_PICKUP"
                    ? "AT PICKUP"
                    : formData.status}
              </Typography>
            )}
          </div>
          <div className="flex flex-col gap-1 w-[260px] ml-2">
            <Typography fontWeight={"700"} fontSize={14}>
              FORWARDER NOTIFY DATE
            </Typography>
            <Typography fontWeight="400" fontSize={16}>
              {null != forwarderNtf && !_.isEmpty(forwarderNtf)
                ? moment(forwarderNtf).format("YYYY-MM-DD HH:mm:ss")
                : "Date Not Available"}
            </Typography>
          </div>

          <div className="flex flex-col gap-1 w-[260px] ml-2">
            <Typography fontWeight={"700"} fontSize={14}>
              FORWARDER NOTIFICATION
            </Typography>
            <Typography fontWeight="400" fontSize={16}>
              {null != forwarderNtfStatus
                ? forwarderNtfStatus
                : "Status Not Available"}
            </Typography>
          </div>
        </div>
        <div className="flex items-start mb-4 pl-5">
          <div className="flex flex-col gap-1 w-[260px]">
            <Typography fontWeight={"700"} fontSize={14}>
              ATA DATE
            </Typography>
            {isUserAdmin && isEditClicked ? (
              <>
                <input
                  type="date"
                  onChange={(e) => handleADDDateChange(e.target.value)}
                  value={formData.ataDate}
                  max={moment().format("YYYY-MM-DD")}
                  className="w-full border-[1.5px] rounded-md p-2 text-sm border-lb-file-up-font-color cursor-pointer hover:border-black focus:outline-none focus:border-black"
                />
                {formErrors.ataDate && (
                  <span className="text-red-600 text-xs mt-1">{formErrors.ataDate}</span>
                )}
                {formErrors.dateComparison && (
                  <span className="text-red-600 text-xs mt-1">{formErrors.dateComparison}</span>
                )}
              </>
            ) : (
              <Typography fontWeight="400" fontSize={16}>
                {formData.ataDate
                  ? new Date(formData.ataDate).toLocaleDateString()
                  : "Date Not Available"}
              </Typography>
            )}
          </div>
          <div className="flex flex-col gap-1 w-[260px] ml-2">
            <Typography fontWeight={"700"} fontSize={14}>
              ATD DATE
            </Typography>
            {isUserAdmin && isEditClicked ? (
              <>
                <input
                  type="date"
                  onChange={(e) => handleATDDateChange(e.target.value)}
                  value={formData.atdDate}
                  max={moment().format("YYYY-MM-DD")}
                  className="w-full border-[1.5px] rounded-md p-2 text-sm border-lb-file-up-font-color cursor-pointer hover:border-black focus:outline-none focus:border-black"
                />
                {formErrors.atdDate && (
                  <span className="text-red-600 text-xs mt-1">{formErrors.atdDate}</span>
                )}
              </>
            ) : (
              <Typography fontWeight="400" fontSize={16}>
                {formData.atdDate
                  ? new Date(formData.atdDate).toLocaleDateString()
                  : "Date Not Available"}
              </Typography>
            )}
          </div>

          <div className="flex flex-col gap-1 w-[260px] ml-2">
            <Typography fontWeight={"700"} fontSize={14}>
              PLANNED LOADING DATE (ETD)
            </Typography>
            {isUserAdmin && isEditClicked && tabValue.buildId != 0 ? (
              <input
                type="datetime-local"
                onChange={(event) =>
                  handleLoadBuildDateChange(event.target.value)
                }
                value={
                  formData.plannedLoadingDate
                    ? moment(formData.plannedLoadingDate)
                      .local()
                      .format("YYYY-MM-DDTHH:mm")
                    : ""
                }
                className="w-full border-[1.5px] rounded-md p-2 text-sm border-lb-file-up-font-color cursor-pointer hover:border-black focus:outline-none focus:border-black"
              />
            ) : (
              <Typography fontWeight="400" fontSize={16}>
                {formData.plannedLoadingDate
                  ? moment(formData.plannedLoadingDate).format("YYYY-MM-DD HH:mm:ss")
                  : "Date Not Available"}
              </Typography>
            )}
          </div>
        </div>

        {/* Row for the Matrix Component */}
        <div className="flex-grow overflow-auto">
          <DateMatrixComponent tabValue={tabValue} />
          <div className="text-center mt-0.25 p-2 sm:p-4">
            <Typography fontWeight="400" fontSize={13}>
              ATA: Actual Time of Arrival | ATD: Actual Time of Departure | ETA:
              Estimated Time of Arrival | ETD: Estimated Time of Departure
            </Typography>
            <Typography fontWeight="700" fontSize={13}>
              *Dates after 01-Oct-2025 are excluded from processing for source ‘Mainchain’ in FOM.
            </Typography>
          </div>
        </div>
      </div>
    );
  })
);

export default DateAndStatusesContent;